// src/pages/Employee/AddEmployee.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import employeeService from '../../services/employee.service';
import companyService from '../../services/company.service';
import Toast from '../../components/Toast/Toast';
import MultiSelect from '../../components/MultiSelect/MultiSelect';
import '../../styles/AddPages.css';

const REPORTING_HIERARCHY = {
  SUPER_ADMIN:   null,
  OFFICE_ADMIN:  null,
  ADMIN:         null,
  ZONAL_MANAGER: 'OFFICE_ADMIN',
  AREA_MANAGER:  'ZONAL_MANAGER',
  EXECUTIVE:     'AREA_MANAGER',
  SALES_OFFICER: 'EXECUTIVE',
  FIELD_AGENT:   'SALES_OFFICER',
  GODOWN:        'OFFICE_ADMIN',
  ACCOUNTS:      'OFFICE_ADMIN',
};

const ROLE_LABELS = {
  ADMIN:         'Admin',
  OFFICE_ADMIN:  'Office Admin',
  ZONAL_MANAGER: 'Zonal Manager',
  AREA_MANAGER:  'Area Manager',
  EXECUTIVE:     'Executive',
  SALES_OFFICER: 'Sales Officer',
  FIELD_AGENT:   'Field Agent',
  GODOWN:        'Godown Incharge',
  ACCOUNTS:      'Accounts',
};

const ROLES_NEEDING_ASSIGNMENT = [
  'ZONAL_MANAGER','AREA_MANAGER','EXECUTIVE','OFFICE_ADMIN',
  'SALES_OFFICER','FIELD_AGENT','GODOWN','ACCOUNTS',
];

// Roles that need the full Area Assignment (Region → HQ → SubHQ → Godown) — UNCHANGED
const ROLES_NEEDING_FULL_AREA = [
  'ZONAL_MANAGER', 'AREA_MANAGER', 'EXECUTIVE',
  'SALES_OFFICER', 'FIELD_AGENT',
];

// Godown role: company → godown → region assignment rows
const ROLES_GODOWN_ONLY = ['GODOWN'];


// Roles that require at least one godown selection
const ROLES_REQUIRING_GODOWN = [
  'ZONAL_MANAGER', 'AREA_MANAGER', 'EXECUTIVE',
  'SALES_OFFICER', 'FIELD_AGENT', 'GODOWN',
];

const ROLES_COMPANY_ONLY = ['ACCOUNTS'];

let rowIdCounter = 0;
const nextRowId = () => `row-${++rowIdCounter}-${Date.now()}`;

const emptyRow = () => ({
  rowId: nextRowId(),
  companyId: '',
  godownId: '',
  selectedRegionIds: [],
  godownOptions: [],
  regionOptions: [],
  loadingGodowns: false,
  loadingRegions: false,
});

const AddEmployee = () => {
  const navigate     = useNavigate();
  const { isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    employeeName:      '',
    employeeIdentityCode: '',
    designationRole:   '',
    reportingTo:       '',
    regionId:          '',
    headquarterIds:    [],
    subHeadquarterIds: [],
    godownIds:         [],
    address:           '',
    email:             '',
    mobile:            '',
    DA:                '',
    bikeTA:            '',
    carTA:             '',
    username:          '',
    password:          '',
    confirmPassword:   '',
    userPhoto:         null,
    dateOfBirth: '',
  });

  const [roles,            setRoles]            = useState([]);
  const [reportingOptions, setReportingOptions] = useState([]);
  const [regions,          setRegions]          = useState([]);
  const [headquarters,     setHeadquarters]     = useState([]);
  const [subHeadquarters,  setSubHeadquarters]  = useState([]);
  const [godowns,          setGodowns]          = useState([]);

  // ── GODOWN role: multi company/godown/region assignment rows ─────────────
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [godownAssignments, setGodownAssignments] = useState([emptyRow()]);

  const [loadingManagers, setLoadingManagers] = useState(false);
  const [loadingRegions,  setLoadingRegions]  = useState(false);
  const [loadingHQs,      setLoadingHQs]      = useState(false);
  const [loadingSubHQs,   setLoadingSubHQs]   = useState(false);
  const [loadingGodowns,  setLoadingGodowns]  = useState(false);

  const [errors,       setErrors]       = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [toast,        setToast]        = useState(null);

  const [companyAccessIds, setCompanyAccessIds] = useState([]);


  useEffect(() => { fetchRoles(); fetchRegions(); }, []);

  const fetchRegions = async () => {
    setLoadingRegions(true);
    try {
      const res = await employeeService.getAllRegions();
      if (res.success && res.data) setRegions(res.data);
    } catch { }
    finally { setLoadingRegions(false); }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const res = await companyService.getAllCompanies();
      if (res.success && res.data) setCompanies(res.data);
    } catch {
      setToast({ message: 'Failed to load companies', type: 'error' });
    } finally { setLoadingCompanies(false); }
  };

  useEffect(() => {
    // Reset everything area-related when role changes
    setFormData(prev => ({
      ...prev,
      reportingTo: '',
      regionId: '',
      headquarterIds: [],
      subHeadquarterIds: [],
      godownIds: [],
    }));
    setReportingOptions([]);
    setHeadquarters([]);
    setSubHeadquarters([]);
    setGodowns([]);
    setGodownAssignments([emptyRow()]);
    setCompanyAccessIds([]);

    const role = formData.designationRole;
    if (!role) return;

    // Load managers (reporting chain)
    const managerRole = REPORTING_HIERARCHY[role];
    if (managerRole) {
      setLoadingManagers(true);
      const companyId = isSuperAdmin ? localStorage.getItem('selectedCompanyId') : undefined;
      employeeService.getUsersByRole(managerRole, companyId)
        .then(res => { if (res.success && res.data) setReportingOptions(res.data); })
        .catch(() => setToast({ message: 'Failed to load managers', type: 'error' }))
        .finally(() => setLoadingManagers(false));
    }

    // For GODOWN role — load company list for the assignment rows
    if (ROLES_GODOWN_ONLY.includes(role)) {
      fetchCompanies();
    }
    if (ROLES_COMPANY_ONLY.includes(role)) { 
    fetchCompanies();
  }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.designationRole]);

  useEffect(() => {
    // Only run this for full-area roles, NOT for GODOWN role
    if (ROLES_GODOWN_ONLY.includes(formData.designationRole)) return;

    setFormData(prev => ({ ...prev, headquarterIds: [], subHeadquarterIds: [], godownIds: [] }));
    setHeadquarters([]); setSubHeadquarters([]); setGodowns([]);
    if (!formData.regionId) return;

    setLoadingHQs(true);
    employeeService.getHeadquartersByRegion(formData.regionId)
      .then(res => { if (res.success && res.data) setHeadquarters(res.data); })
      .finally(() => setLoadingHQs(false));

    setLoadingGodowns(true);
    employeeService.getGodownsByRegion(formData.regionId)
      .then(res => { if (res.success && res.data) setGodowns(res.data); })
      .finally(() => setLoadingGodowns(false));
  }, [formData.regionId, formData.designationRole]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, subHeadquarterIds: [] }));
    setSubHeadquarters([]);
    if (!formData.headquarterIds || formData.headquarterIds.length === 0) return;
    setLoadingSubHQs(true);
    Promise.all(
      formData.headquarterIds.map(hqId =>
        employeeService.getSubHeadquartersByHQ(hqId).then(r => r.success ? r.data : [])
      )
    ).then(results => {
      const all    = results.flat();
      const unique = Array.from(new Map(all.map(s => [s.id, s])).values());
      setSubHeadquarters(unique);
    }).finally(() => setLoadingSubHQs(false));
  }, [formData.headquarterIds]);

  const setFallbackRoles = () => setRoles([
    { id: 1, name: 'OFFICE_ADMIN',  displayName: 'Office Admin',     level: 100 },
    { id:2,  name:'ADMIN',         displayName:'Admin',            level:90 },
    { id:3,  name:'ZONAL_MANAGER', displayName:'Zonal Manager',    level:70 },
    { id:4,  name:'AREA_MANAGER',  displayName:'Area Manager',     level:60 },
    { id:5,  name:'EXECUTIVE',     displayName:'Executive',        level:50 },
    { id:6,  name:'SALES_OFFICER', displayName:'Sales Officer',    level:30 },
    { id:7,  name:'ACCOUNTS',      displayName:'Accounts',         level:25 },
    { id:8,  name:'FIELD_AGENT',   displayName:'Field Agent',      level:20 },
    { id:9,  name:'GODOWN',        displayName:'Go Down Incharge', level:10 },
  ]);

  const fetchRoles = async () => {
    try {
      const r = await employeeService.getRoles();
      if (r?.success && Array.isArray(r.data)) setRoles(r.data);
      else setFallbackRoles();
    } catch { setFallbackRoles(); }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      if (file) {
        const r = new FileReader();
        r.onloadend = () => setPhotoPreview(r.result);
        r.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // ── Godown assignment row handlers ────────────────────────────────────────

  const updateRow = (rowId, patch) => {
    setGodownAssignments(prev => prev.map(r => r.rowId === rowId ? { ...r, ...patch } : r));
  };

  const handleRowCompanyChange = async (rowId, companyId) => {
    updateRow(rowId, {
      companyId, godownId: '', selectedRegionIds: [],
      godownOptions: [], regionOptions: [], loadingGodowns: !!companyId,
    });
    if (errors.godownAssignments) setErrors(prev => ({ ...prev, godownAssignments: '' }));
    if (!companyId) return;
    try {
      const res = await employeeService.getGodownsByCompany(companyId);
      updateRow(rowId, { godownOptions: res.success ? (res.data || []) : [] });
    } catch {
      setToast({ message: 'Failed to load godowns for this company', type: 'error' });
    } finally {
      updateRow(rowId, { loadingGodowns: false });
    }
  };

  const handleRowGodownChange = async (rowId, godownId, companyId) => {
    updateRow(rowId, { godownId, selectedRegionIds: [], regionOptions: [], loadingRegions: !!godownId });
    if (!godownId || !companyId) return;
    try {
      const res = await employeeService.getRegionsForGodown(godownId, companyId);
      updateRow(rowId, { regionOptions: res.success ? (res.data || []) : [] });
    } catch {
      setToast({ message: 'Failed to load regions for this godown', type: 'error' });
    } finally {
      updateRow(rowId, { loadingRegions: false });
    }
  };

  const handleRowRegionsChange = (rowId, regionIds) => {
    updateRow(rowId, { selectedRegionIds: regionIds });
    if (errors.godownAssignments) setErrors(prev => ({ ...prev, godownAssignments: '' }));
  };

  const addAssignmentRow = () => setGodownAssignments(prev => [...prev, emptyRow()]);

  const removeAssignmentRow = (rowId) => {
    setGodownAssignments(prev => {
      const next = prev.filter(r => r.rowId !== rowId);
      return next.length > 0 ? next : [emptyRow()];
    });
  };

  const validate = () => {
    const e = {};
    if (!formData.employeeName.trim())         e.employeeName         = 'Required';
    if (!formData.employeeIdentityCode.trim()) e.employeeIdentityCode = 'Required';
    if (!formData.designationRole)             e.designationRole      = 'Required';
    const mgr = REPORTING_HIERARCHY[formData.designationRole];
    if (mgr && !formData.reportingTo)
      e.reportingTo = `Select the ${ROLE_LABELS[mgr]} this employee reports to`;

    if (ROLES_GODOWN_ONLY.includes(formData.designationRole)) {
      const validRows = godownAssignments.filter(r => r.companyId && r.godownId && r.selectedRegionIds.length > 0);
      if (validRows.length === 0) {
        e.godownAssignments = 'Add at least one complete company → godown → region assignment';
      }
    } else if (ROLES_REQUIRING_GODOWN.includes(formData.designationRole) &&
      (!formData.godownIds || formData.godownIds.length === 0)) {
      e.godownIds = 'Please select at least one Godown for this role';
    }

    if (ROLES_COMPANY_ONLY.includes(formData.designationRole) && companyAccessIds.length === 0) {
    e.companyAccessIds = 'Select at least one company for this Accounts user';
  }

    if (!formData.email.trim())                e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email';
    if (!formData.mobile.trim())               e.mobile = 'Required';
    else if (!/^\d{10}$/.test(formData.mobile)) e.mobile = 'Must be 10 digits';
    if (!formData.username.trim())             e.username = 'Required';
    else if (formData.username.length < 3)     e.username = 'Min 3 characters';
    if (!formData.password)                    e.password = 'Required';
    else if (formData.password.length < 8)     e.password = 'Min 8 characters';
    if (formData.password !== formData.confirmPassword)
      e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const parts     = formData.employeeName.trim().split(' ');
      const firstName = parts[0] || '';
      const lastName  = parts.slice(1).join(' ') || parts[0];
      const selRole   = roles.find(r => r.name === formData.designationRole);
      if (!selRole) {
        setToast({ message: 'Select a valid role', type: 'error' });
        setIsSubmitting(false);
        return;
      }

      const isGodownRole = ROLES_GODOWN_ONLY.includes(formData.designationRole);

      // Flatten assignment rows into (godownId, regionId) grant pairs
      const godownAccess = isGodownRole
        ? godownAssignments
            .filter(r => r.companyId && r.godownId && r.selectedRegionIds.length > 0)
            .flatMap(r => r.selectedRegionIds.map(regionId => ({
              godownId: parseInt(r.godownId),
              regionId: parseInt(regionId),
            })))
        : [];

      // Legacy flat godownIds — distinct godown ids across all rows, kept for
      // backward-compat with the old single-FK / user_godowns write path
      const legacyGodownIds = isGodownRole
        ? [...new Set(godownAccess.map(g => g.godownId))]
        : formData.godownIds;

      const payload = {
        username:     formData.username,
        email:        formData.email,
        password:     formData.password,
        firstName,
        lastName,
        roleId:       selRole.id,
        employeeCode: formData.employeeIdentityCode,
        phone:        formData.mobile || undefined,
        DA:           formData.DA     ? parseFloat(formData.DA)     : undefined,
        bikeTA:       formData.bikeTA ? parseFloat(formData.bikeTA) : undefined,
        carTA:        formData.carTA  ? parseFloat(formData.carTA)  : undefined,
        reportingTo:  formData.reportingTo ? parseInt(formData.reportingTo) : undefined,
        regionId:          formData.regionId               ? parseInt(formData.regionId)        : undefined,
        headquarterIds:    formData.headquarterIds?.length    > 0 ? formData.headquarterIds    : undefined,
        subHeadquarterIds: formData.subHeadquarterIds?.length > 0 ? formData.subHeadquarterIds : undefined,
        godownIds:         legacyGodownIds?.length             > 0 ? legacyGodownIds           : undefined,
        godownAccess:      godownAccess.length > 0 ? godownAccess : undefined,
        dateOfBirth: formData.dateOfBirth || undefined,
      };

      payload.companyAccess = ROLES_COMPANY_ONLY.includes(role) && companyAccessIds.length > 0
  ? companyAccessIds
  : undefined;

      if (isSuperAdmin) {
        const globalRoles = ['OFFICE_ADMIN', 'GODOWN', 'ACCOUNTS'];
        const isGlobalRole = globalRoles.includes(formData.designationRole);

        if (!isGlobalRole) {
          const cid = localStorage.getItem('selectedCompanyId');
          if (!cid) {
            setToast({ message: 'Select a company first', type: 'error' });
            setIsSubmitting(false);
            return;
          }
          payload.companyId = parseInt(cid);
        }
      }

      const res = await employeeService.createEmployee(payload);
      if (res.success) {
        setToast({ message: 'Employee added successfully!', type: 'success' });
        setTimeout(() => navigate('/employee/view'), 1500);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to add employee', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const role = formData.designationRole;
  const mgr = REPORTING_HIERARCHY[role];
  const managerLabel = mgr ? ROLE_LABELS[mgr] || '' : '';
  const showReporting = role && mgr !== null && mgr !== undefined;

  const showFullArea   = ROLES_NEEDING_FULL_AREA.includes(role);
  const showGodownOnly = ROLES_GODOWN_ONLY.includes(role);
  const godownRequired = ROLES_REQUIRING_GODOWN.includes(role);


  return (
    <div className="add-employee-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/employee/view">Employees</a></li>
          <li className="breadcrumb-item active">Add Employee</li>
        </ol>
      </nav>
      <div className="page-header"><h4 className="page-title">Add New Employee</h4></div>
      <div className="form-card">
        <form onSubmit={handleSubmit}>
          {/* Role & Identity */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Employee Name <span className="required">*</span></label>
                <input type="text" name="employeeName" className={`form-input ${errors.employeeName?'error':''}`}
                  value={formData.employeeName} onChange={handleChange} placeholder="Enter full name"/>
                {errors.employeeName && <span className="error-message">{errors.employeeName}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Employee Identity Code <span className="required">*</span></label>
                <input type="text" name="employeeIdentityCode" className={`form-input ${errors.employeeIdentityCode?'error':''}`}
                  value={formData.employeeIdentityCode} onChange={handleChange} placeholder="Enter employee code"/>
                {errors.employeeIdentityCode && <span className="error-message">{errors.employeeIdentityCode}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Designation Role <span className="required">*</span></label>
                <select name="designationRole" className={`form-input ${errors.designationRole?'error':''}`}
                  value={formData.designationRole} onChange={handleChange}>
                  <option value="">Select Role</option>
                  {roles.map(r => <option key={r.id} value={r.name}>{r.displayName||r.name}</option>)}
                </select>
                {errors.designationRole && <span className="error-message">{errors.designationRole}</span>}
              </div>
            </div>
            {showReporting && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Reporting To ({managerLabel}) <span className="required">*</span></label>
                  {loadingManagers ? (
                    <div className="form-input" style={{color:'#adb5bd',cursor:'default'}}>Loading {managerLabel} list...</div>
                  ) : reportingOptions.length === 0 ? (
                    <div>
                      <select className={`form-input ${errors.reportingTo?'error':''}`} disabled>
                        <option>No {managerLabel} found — add one first</option>
                      </select>
                      <small style={{color:'#e53935',fontSize:12}}>⚠️ Add a {managerLabel} before this role.</small>
                    </div>
                  ) : (
                    <select name="reportingTo" className={`form-input ${errors.reportingTo?'error':''}`}
                      value={formData.reportingTo} onChange={handleChange}>
                      <option value="">Select {managerLabel}</option>
                      {reportingOptions.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.firstName} {emp.lastName}{emp.employeeCode?` (${emp.employeeCode})`:''}
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.reportingTo && <span className="error-message">{errors.reportingTo}</span>}
                </div>
              </div>
            )}
          </div>

          {/* Area Assignment — Full (region/HQ/subHQ/godown) for field roles — UNCHANGED */}
          {showFullArea && (
            <div className="form-section">
              <h3 style={{color:'#4caf50',fontSize:13,fontWeight:700,marginBottom:16,textTransform:'uppercase',letterSpacing:'0.5px'}}>
                📍 Area Assignment
              </h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Region</label>
                  {loadingRegions ? (
                    <div className="form-input" style={{color:'#adb5bd',cursor:'default'}}>Loading regions...</div>
                  ) : (
                    <select name="regionId" className="form-input" value={formData.regionId} onChange={handleChange}>
                      <option value="">Select Region</option>
                      {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  )}
                </div>
                <MultiSelect label="Headquarters" options={headquarters} selected={formData.headquarterIds}
                  onChange={(ids) => setFormData(prev => ({ ...prev, headquarterIds: ids }))}
                  disabled={!formData.regionId} loading={loadingHQs}
                  placeholder={!formData.regionId ? 'Select Region first' : 'Select Headquarters'}
                  emptyMsg="No headquarters in this region"/>
                <MultiSelect label="Sub-Headquarters" options={subHeadquarters} selected={formData.subHeadquarterIds}
                  onChange={(ids) => setFormData(prev => ({ ...prev, subHeadquarterIds: ids }))}
                  disabled={!formData.headquarterIds||formData.headquarterIds.length===0} loading={loadingSubHQs}
                  placeholder={!formData.headquarterIds?.length ? 'Select HQ first' : 'Select Sub-Headquarters'}
                  emptyMsg="No sub-headquarters for selected HQs"/>
              </div>
              <div className="form-row">
                <MultiSelect
                  label={
                    <span>
                      Godowns
                      {godownRequired ? <span className="required"> *</span> : <span style={{ fontWeight:400, color:'#adb5bd' }}> (Optional)</span>}
                    </span>
                  }
                  options={godowns}
                  selected={formData.godownIds}
                  onChange={(ids) => {
                    setFormData(prev => ({ ...prev, godownIds: ids }));
                    if (errors.godownIds) setErrors(prev => ({ ...prev, godownIds: '' }));
                  }}
                  disabled={!formData.regionId}
                  loading={loadingGodowns}
                  placeholder={!formData.regionId ? 'Select Region first' : 'Select Godowns'}
                  emptyMsg="No godowns in this region"
                />
                {errors.godownIds && <span className="error-message">{errors.godownIds}</span>}
              </div>
            </div>
          )}

          {/* Area Assignment — Godown role: company → godown → region rows */}
          {showGodownOnly && (
            <div className="form-section">
              <h3 style={{color:'#4caf50',fontSize:13,fontWeight:700,marginBottom:16,textTransform:'uppercase',letterSpacing:'0.5px'}}>
                🏬 Godown Assignment
              </h3>
              <p style={{fontSize:13, color:'#6c757d', margin:'-10px 0 16px 0'}}>
                Godown users can be assigned to multiple companies. For each company, pick which godown they'll manage and which of that company's regions they can see orders for.
              </p>

              {godownAssignments.map((row, idx) => (
                <div key={row.rowId} style={{
                  border: '1px solid #e9ecef', borderRadius: 10, padding: 16,
                  marginBottom: 12, background: '#fafbfc', position: 'relative',
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 12 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#495057' }}>Assignment {idx + 1}</span>
                    {godownAssignments.length > 1 && (
                      <button type="button" onClick={() => removeAssignmentRow(row.rowId)}
                        style={{ background:'none', border:'none', color:'#e53935', fontSize:12, cursor:'pointer', fontWeight:600 }}>
                        ✕ Remove
                      </button>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Company <span className="required">*</span></label>
                      {loadingCompanies ? (
                        <div className="form-input" style={{color:'#adb5bd',cursor:'default'}}>Loading companies...</div>
                      ) : (
                        <select className="form-input" value={row.companyId}
                          onChange={(e) => handleRowCompanyChange(row.rowId, e.target.value)}>
                          <option value="">Select Company</option>
                          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Godown <span className="required">*</span></label>
                      {row.loadingGodowns ? (
                        <div className="form-input" style={{color:'#adb5bd',cursor:'default'}}>Loading godowns...</div>
                      ) : (
                        <select className="form-input" value={row.godownId} disabled={!row.companyId}
                          onChange={(e) => handleRowGodownChange(row.rowId, e.target.value, row.companyId)}>
                          <option value="">{!row.companyId ? 'Select Company first' : row.godownOptions.length === 0 ? 'No godowns for this company' : 'Select Godown'}</option>
                          {row.godownOptions.map(g => <option key={g.id} value={g.id}>{g.name} — {g.location}</option>)}
                        </select>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <MultiSelect
                      label={<span>Regions <span className="required">*</span></span>}
                      options={row.regionOptions}
                      selected={row.selectedRegionIds}
                      onChange={(ids) => handleRowRegionsChange(row.rowId, ids)}
                      disabled={!row.godownId}
                      loading={row.loadingRegions}
                      placeholder={!row.godownId ? 'Select Godown first' : 'Select Regions'}
                      emptyMsg="This godown has no regions linked for this company"
                    />
                  </div>
                </div>
              ))}

              {errors.godownAssignments && (
                <span className="error-message" style={{ display:'block', marginBottom: 8 }}>
                  {errors.godownAssignments}
                </span>
              )}

              <button type="button" onClick={addAssignmentRow}
                style={{
                  background:'#e8f5e9', color:'#2e7d32', border:'1px dashed #2e7d32',
                  borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor:'pointer',
                }}>
                + Add another company / godown assignment
              </button>
            </div>
          )}

          {ROLES_COMPANY_ONLY.includes(role) && (
  <div className="form-section">
    <h3 style={{color:'#4caf50',fontSize:13,fontWeight:700,marginBottom:16,textTransform:'uppercase'}}>
      🏢 Company Access
    </h3>
    <p style={{fontSize:13, color:'#6c757d', margin:'-10px 0 16px 0'}}>
      Accounts users are not tied to a single company. Select which companies this user can view payments and orders for.
    </p>
    <MultiSelect
      label={<span>Companies <span className="required">*</span></span>}
      options={companies.map(c => ({ id: c.id, name: c.name }))}
      selected={companyAccessIds}
      onChange={(ids) => {
    setCompanyAccessIds(ids);
    if (errors.companyAccessIds) setErrors(prev => ({ ...prev, companyAccessIds: '' }));   // ✅
  }}
      loading={loadingCompanies}
      placeholder="Select Companies"
      emptyMsg="No companies available"
    />
    {errors.companyAccessIds && <span className="error-message">{errors.companyAccessIds}</span>}
  </div>
)}

          {/* OFFICE_ADMIN / ACCOUNTS / ADMIN → no Area Assignment section at all */}

          {/* Address */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group form-group-full">
                <label className="form-label">Address</label>
                <textarea name="address" className="form-textarea" value={formData.address}
                  onChange={handleChange} placeholder="Enter complete address" rows="3"/>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email <span className="required">*</span></label>
                <input type="email" name="email" className={`form-input ${errors.email?'error':''}`}
                  value={formData.email} onChange={handleChange} placeholder="Enter email"/>
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Mobile <span className="required">*</span></label>
                <input type="tel" name="mobile" maxLength="10" className={`form-input ${errors.mobile?'error':''}`}
                  value={formData.mobile} onChange={handleChange} placeholder="10-digit mobile"/>
                {errors.mobile && <span className="error-message">{errors.mobile}</span>}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Date of Birth</label>
              <input type="date" name="dateOfBirth" className="form-input"
                value={formData.dateOfBirth} onChange={handleChange} />
            </div>
          </div>

          {/* Allowances */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">DA</label>
                <input type="number" name="DA" className="form-input" value={formData.DA}
                  onChange={handleChange} placeholder="Dearness Allowance" step="0.01"/>
              </div>
              <div className="form-group">
                <label className="form-label">Bike TA</label>
                <input type="number" name="bikeTA" className="form-input" value={formData.bikeTA}
                  onChange={handleChange} placeholder="Bike Travel Allowance" step="0.01"/>
              </div>
              <div className="form-group">
                <label className="form-label">Car TA</label>
                <input type="number" name="carTA" className="form-input" value={formData.carTA}
                  onChange={handleChange} placeholder="Car Travel Allowance" step="0.01"/>
              </div>
            </div>
          </div>

          {/* Account */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Username <span className="required">*</span></label>
                <input type="text" name="username" className={`form-input ${errors.username?'error':''}`}
                  value={formData.username} onChange={handleChange} placeholder="Enter username"/>
                {errors.username && <span className="error-message">{errors.username}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Password <span className="required">*</span></label>
                <input type="password" name="password" className={`form-input ${errors.password?'error':''}`}
                  value={formData.password} onChange={handleChange} placeholder="Min 8 characters"/>
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Confirm Password <span className="required">*</span></label>
                <input type="password" name="confirmPassword" className={`form-input ${errors.confirmPassword?'error':''}`}
                  value={formData.confirmPassword} onChange={handleChange} placeholder="Confirm password"/>
                {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
              </div>
            </div>
          </div>

          {/* Photo */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Choose Photo</label>
                <input type="file" name="userPhoto" className="form-input" onChange={handleChange} accept="image/*"/>
                {photoPreview && <div className="photo-preview-large"><img src={photoPreview} alt="Preview"/></div>}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/employee/view')} disabled={isSubmitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Adding Employee...' : 'Add Employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEmployee;